/**
 * Сорта и гибриды — platform-admin (reference.varieties).
 *
 * Каталог сортов существовал только в БД: таблица с миграции 001, резолвер и
 * очередь разбора с 088 — и ни одного экрана. Сорт нельзя было ни посмотреть,
 * ни завести, ни разобрать из очереди, хотя история поля приезжает склейкой
 * «культура + сорт + репродукция» и без каталога сортов её некуда раскладывать.
 *
 * Два таба:
 *   • «Каталог» — список с фильтром по культуре + создание/правка/удаление;
 *   • «Разбор»  — очередь reference.variety_import_queue: привязать термин к
 *                 существующему сорту (учится alias) или завести новый.
 *
 * Сорт ВСЕГДА принадлежит культуре, поэтому культура — обязательное поле формы,
 * а не фильтр «для удобства»: одно торговое имя легально живёт под разными
 * культурами, и без культуры привязка неоднозначна.
 */
import { useMemo, useState } from 'react';
import { Link2, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  EntityPicker,
  type EntityOption,
} from '@/pages/reference/pesticides/EntityPicker';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import {
  useCreateVariety,
  useCreateVarietyFromQueue,
  useDeleteVariety,
  useDeleteVarietyQueueItem,
  useMapVarietyQueueItem,
  useUpdateVariety,
  useVarieties,
  useVarietyQueue,
} from './queries';
import type { Variety, VarietyQueueItem } from './types';

const CROP_SOURCE = { path: '/api/v1/reference/crops', translated: true };
const VARIETY_SOURCE = { path: '/api/v1/reference/varieties', translated: true };

function ruName(v: Variety): string {
  return v.name_translations?.ru ?? '—';
}


// ─── форма сорта (создание / правка) ──────────────────────────────────────

function VarietyDialog({
  mode,
  initial,
  defaultCrop,
  onClose,
}: {
  mode: 'create' | 'edit';
  initial: Variety | null;
  defaultCrop: EntityOption | null;
  onClose: () => void;
}) {
  const [crop, setCrop] = useState<EntityOption | null>(
    initial ? { id: initial.crop_id, name: initial.crop_name ?? '—' } : defaultCrop,
  );
  const [name, setName] = useState(initial ? ruName(initial) : '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [originator, setOriginator] = useState(initial?.originator ?? '');
  const [isHybrid, setIsHybrid] = useState(initial?.is_hybrid ?? false);

  const createMut = useCreateVariety();
  const updateMut = useUpdateVariety();
  const pending = createMut.isPending || updateMut.isPending;

  const submit = async () => {
    try {
      if (mode === 'create') {
        if (!crop) return;
        await createMut.mutateAsync({
          crop_id: crop.id,
          name_translations: { ru: name.trim() },
          code: code.trim() || undefined,
          originator: originator.trim() || undefined,
          is_hybrid: isHybrid,
        });
        toast.success(`Сорт «${name.trim()}» создан`);
      } else if (initial) {
        await updateMut.mutateAsync({
          id: initial.id,
          payload: {
            name_translations: { ru: name.trim() },
            code: code.trim() || undefined,
            originator: originator.trim() || undefined,
            is_hybrid: isHybrid,
          },
        });
        toast.success('Сорт обновлён');
      }
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Новый сорт' : 'Правка сорта'}
          </DialogTitle>
          <DialogDescription>
            Сорт принадлежит культуре. Одно торговое имя может существовать под
            разными культурами — это не дубль.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label>Культура</Label>
            {mode === 'create' ? (
              <EntityPicker
                source={CROP_SOURCE}
                value={crop}
                onChange={setCrop}
                placeholder="Выберите культуру…"
              />
            ) : (
              // Перенос сорта под другую культуру — не правка карточки, а
              // перепривязка с каскадом по посевам. Здесь read-only намеренно.
              <p className="text-sm text-muted-foreground">
                {crop?.name ?? '—'} · сменить культуру у существующего сорта нельзя
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="v-name">Название (ru)</Label>
            <Input
              id="v-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="v-orig">Оригинатор</Label>
            <Input
              id="v-orig"
              value={originator}
              onChange={(e) => setOriginator(e.target.value)}
              placeholder="НЦЗ им. П. П. Лукьяненко"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="v-code">Код (необязательно)</Label>
            <Input
              id="v-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="v-hybrid"
              checked={isHybrid}
              onCheckedChange={setIsHybrid}
            />
            <Label htmlFor="v-hybrid">Гибрид</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={pending || !name.trim() || (mode === 'create' && !crop)}
          >
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── таб «Каталог» ────────────────────────────────────────────────────────

function CatalogTab() {
  const [crop, setCrop] = useState<EntityOption | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<
    { mode: 'create'; initial: null } | { mode: 'edit'; initial: Variety } | null
  >(null);

  const query = useMemo(
    () => ({ crop_id: crop?.id, search: search || undefined, limit: 200 }),
    [crop, search],
  );
  const q = useVarieties(query);
  const deleteMut = useDeleteVariety();

  const items = q.data?.items ?? [];

  const remove = async (v: Variety) => {
    try {
      await deleteMut.mutateAsync(v.id);
      toast.success(`Сорт «${ruName(v)}» удалён`);
    } catch (err) {
      // 409 usage-гейта — не сбой, а «сорт занят»: показываем где именно.
      toast.error(describeApiError(err));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[220px]">
            <EntityPicker
              source={CROP_SOURCE}
              value={crop}
              onChange={setCrop}
              placeholder="Все культуры"
            />
          </div>
          <form
            className="relative min-w-[200px] flex-1 sm:max-w-xs"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput.trim());
            }}
          >
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onBlur={() => setSearch(searchInput.trim())}
              placeholder="Сорт, код, оригинатор…"
              className="pl-8"
            />
          </form>
          <Button
            className="ml-auto"
            onClick={() => setDialog({ mode: 'create', initial: null })}
          >
            <Plus className="mr-2 h-4 w-4" />
            Добавить сорт
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            Сортов не найдено.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Сорт</TableHead>
                <TableHead className="w-[200px]">Культура</TableHead>
                <TableHead className="w-[220px]">Оригинатор</TableHead>
                <TableHead className="w-[100px]">Тип</TableHead>
                <TableHead className="w-[120px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="font-medium">
                    {ruName(v)}
                    {v.code && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {v.code}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {v.crop_name ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {v.originator ?? '—'}
                  </TableCell>
                  <TableCell>
                    {v.is_hybrid ? (
                      <Badge variant="outline">гибрид</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">сорт</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Править «${ruName(v)}»`}
                        onClick={() => setDialog({ mode: 'edit', initial: v })}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Удалить «${ruName(v)}»`}
                        onClick={() => remove(v)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {dialog && (
        <VarietyDialog
          key={dialog.mode === 'edit' ? dialog.initial.id : 'create'}
          mode={dialog.mode}
          initial={dialog.mode === 'edit' ? dialog.initial : null}
          defaultCrop={crop}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

// ─── таб «Разбор» ─────────────────────────────────────────────────────────

function QueueMapDialog({
  item,
  onClose,
}: {
  item: VarietyQueueItem;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<EntityOption | null>(null);
  const mapMut = useMapVarietyQueueItem();

  const submit = async () => {
    if (!choice) return;
    try {
      await mapMut.mutateAsync({ id: item.id, varietyId: choice.id });
      toast.success(`«${item.incoming_name}» → «${choice.name}»`);
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Привязать к сорту</DialogTitle>
          <DialogDescription>
            «{item.incoming_name}» станет синонимом выбранного сорта. Синоним
            скоупится культурой этого сорта.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Сорт справочника</Label>
          <EntityPicker
            source={VARIETY_SOURCE}
            value={choice}
            onChange={setChoice}
            placeholder="Найдите сорт…"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={!choice || mapMut.isPending}>
            {mapMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Привязать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QueueCreateDialog({
  item,
  onClose,
}: {
  item: VarietyQueueItem;
  onClose: () => void;
}) {
  const [crop, setCrop] = useState<EntityOption | null>(
    item.crop_id ? { id: item.crop_id, name: item.crop_name ?? '—' } : null,
  );
  const [name, setName] = useState(item.incoming_name);
  const [isHybrid, setIsHybrid] = useState(false);
  const createMut = useCreateVarietyFromQueue();

  const submit = async () => {
    if (!crop) return;
    try {
      await createMut.mutateAsync({
        id: item.id,
        payload: { crop_id: crop.id, name_ru: name.trim(), is_hybrid: isHybrid },
      });
      toast.success(`Сорт «${name.trim()}» заведён`);
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Завести сорт из термина</DialogTitle>
          <DialogDescription>
            Культура обязательна: сорт вне культуры существовать не может.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label>Культура</Label>
            <EntityPicker
              source={CROP_SOURCE}
              value={crop}
              onChange={setCrop}
              placeholder="Выберите культуру…"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="qv-name">Название (ru)</Label>
            <Input
              id="qv-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="qv-hybrid"
              checked={isHybrid}
              onCheckedChange={setIsHybrid}
            />
            <Label htmlFor="qv-hybrid">Гибрид</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={!crop || !name.trim() || createMut.isPending}>
            {createMut.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QueueTab() {
  const [mapTarget, setMapTarget] = useState<VarietyQueueItem | null>(null);
  const [createTarget, setCreateTarget] = useState<VarietyQueueItem | null>(null);
  const q = useVarietyQueue({ status: 'pending' });
  const deleteMut = useDeleteVarietyQueueItem();

  const items = q.data?.items ?? [];
  const summary = q.data?.summary;

  return (
    <div className="space-y-4">
      {summary && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="secondary">На разборе: {summary.pending}</Badge>
          <Badge variant="outline">
            без культуры: {summary.pending_without_crop}
          </Badge>
          <Badge variant="outline">Привязано: {summary.mapped}</Badge>
          <Badge variant="outline">Заведено: {summary.created}</Badge>
        </div>
      )}
      <Card className="overflow-hidden">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            Очередь пуста — все сорта разобраны.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Термин</TableHead>
                <TableHead className="w-[200px]">Культура</TableHead>
                <TableHead className="w-[180px]">Откуда</TableHead>
                <TableHead className="w-[80px] text-right">Вхождений</TableHead>
                <TableHead className="w-[220px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    {item.incoming_name}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {item.crop_name ?? (
                      <span className="text-destructive">не определена</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {item.tenant_name ?? 'системный импорт'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {item.occurrence_count}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setMapTarget(item)}
                      >
                        <Link2 className="mr-1 h-3.5 w-3.5" />
                        Привязать
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCreateTarget(item)}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Завести
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Убрать «${item.incoming_name}» из очереди`}
                        onClick={async () => {
                          try {
                            await deleteMut.mutateAsync(item.id);
                            toast.success('Снято с очереди');
                          } catch (err) {
                            toast.error(describeApiError(err));
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {mapTarget && (
        <QueueMapDialog
          key={mapTarget.id}
          item={mapTarget}
          onClose={() => setMapTarget(null)}
        />
      )}
      {createTarget && (
        <QueueCreateDialog
          key={createTarget.id}
          item={createTarget}
          onClose={() => setCreateTarget(null)}
        />
      )}
    </div>
  );
}

// ─── страница ─────────────────────────────────────────────────────────────

export function PlatformAdminVarietiesPage() {
  const [tab, setTab] = useState<'catalog' | 'queue'>('catalog');

  return (
    <PlatformAdminLayout>
      <div className="space-y-4">
        <header>
          <h1 className="text-xl font-semibold text-foreground">
            Сорта и гибриды
          </h1>
          <p className="text-sm text-muted-foreground">
            Каталог сортов справочника и разбор непривязанных сортов из импорта.
          </p>
        </header>
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'catalog' | 'queue')}>
          <TabsList>
            <TabsTrigger value="catalog">Каталог</TabsTrigger>
            <TabsTrigger value="queue">Разбор</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'catalog' ? <CatalogTab /> : <QueueTab />}
      </div>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminVarietiesPage;
